import $api, { API_URL } from "@/shared/http";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { IApiError, LoginAsData, LoginData } from "./types";
import { revertAll } from "./actions";
import { deleteCookie } from "@/shared/helpers/DeleteCookie";

interface IApp {
  isAuth: boolean;
  error: string | null | unknown;
  token: string | null;
  isLoading: boolean;
  isRigthPlateOpen: boolean;
  rightPlateChildrenType: string;
  showAuthModal: boolean;
  serverTime: string | null;
  isFileLoading: boolean;
}
const initialState: IApp = {
  isAuth: false,
  error: "",
  token: null,
  isLoading: false,
  isRigthPlateOpen: false,
  rightPlateChildrenType: "",
  showAuthModal: false,
  serverTime: null,
  isFileLoading: false,
};
export const login = createAsyncThunk(
  "application/login",
  async (data: LoginData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/user/login`, data, {
        headers: { "Access-Control-Allow-Origin": "*" },
      });
      if (response.status === 200) {
        const token = response.data?.accessToken;
        document.cookie = `access-token=${token}; path=/;`;
        return true;
      }
      return false;
    } catch (error) {
      const axiosError = error as {
        response?: {
          data: IApiError;
          status: number;
        };
        message: string;
      };

      if (error.response.status === 500) {
        return rejectWithValue({
          title: "Ошибка",
          status: 500,
          detail: "Ошибка при авторизации, Обратитесь в службу поддержки",
        });
      }

      return rejectWithValue(
        axiosError.response?.data || {
          title: "Ошибка",
          status: axiosError.response?.status || 500,
          detail: axiosError.message || "Неизвестная ошибка",
        },
      );
    }
  },
);
export const loginAs = createAsyncThunk(
  "application/login-as",
  async (data: LoginAsData, { rejectWithValue }) => {
    try {
      const response = await $api.post(
        `/admin/impersonate/${data.targetUserId}`,
      );
      if (response.status === 200) {
        const token = response.data?.accessToken;
        document.cookie = `access-token=${token}; path=/;`;
        return true;
      }
      return false;
    } catch (error) {
      const axiosError = error as {
        response?: {
          data: IApiError;
          status: number;
        };
        message: string;
      };

      if (error.response.status === 500) {
        return rejectWithValue({
          title: "Ошибка",
          status: 500,
          detail: "Ошибка при авторизации, Обратитесь в службу поддержки",
        });
      }

      return rejectWithValue(
        axiosError.response?.data || {
          title: "Ошибка",
          status: axiosError.response?.status || 500,
          detail: axiosError.message || "Неизвестная ошибка",
        },
      );
    }
  },
);
export const getServerTime = createAsyncThunk(
  "application/serverTime",
  async (_, { rejectWithValue }) => {
    try {
      const response = await $api.get(`/testclock`);
      if (response.status === 200) {
        return response.data;
      }
    } catch (error) {
      const axiosError = error as {
        response?: {
          data: IApiError;
          status: number;
        };
        message: string;
      };

      if (error.response.status === 500) {
        return rejectWithValue({
          title: "Ошибка",
          status: 500,
          detail: "Ошибка при авторизации, Обратитесь в службу поддержки",
        });
      }

      return rejectWithValue(
        axiosError.response?.data || {
          title: "Ошибка",
          status: axiosError.response?.status || 500,
          detail: axiosError.message || "Неизвестная ошибка",
        },
      );
    }
  },
);

export const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {
    setRightPlateOpen(state, action: PayloadAction<boolean>) {
      state.isRigthPlateOpen = action.payload;
      if (!action.payload) {
        state.rightPlateChildrenType = "";
      }
    },
    setRightPlateChildrenType(state, action: PayloadAction<string>) {
      state.rightPlateChildrenType = action.payload;
    },
    logout(state) {
      deleteCookie("access-token");
      deleteCookie("access-token", "/users");
      state.isAuth = false;
    },
    setShowAuthModal(state, action: PayloadAction<boolean>) {
      state.showAuthModal = action.payload;
    },
    setIsFileLoading(state, action: PayloadAction<boolean>) {
      state.isFileLoading = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(login.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(login.fulfilled, (state, action) => {
      state.isAuth = !!action.payload;
      state.showAuthModal = false;
      state.error = null;
      state.isLoading = false;
    });

    builder.addCase(login.rejected, (state, action) => {
      state.error = action.payload;
      state.isAuth = false;
      state.isLoading = false;
    });

    builder.addCase(loginAs.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(loginAs.fulfilled, (state, action) => {
      state.isAuth = !!action.payload;
      state.showAuthModal = false;
      state.error = null;
      state.isLoading = false;
    });

    builder.addCase(loginAs.rejected, (state, action) => {
      state.error = action.payload;
      state.isAuth = false;
      state.isLoading = false;
    });

    builder.addCase(getServerTime.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });

    builder.addCase(getServerTime.fulfilled, (state, action) => {
      state.serverTime = action.payload.utcNow;
      state.error = null;
      state.isLoading = false;
    });

    builder.addCase(getServerTime.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
  },
});

export const {
  setRightPlateOpen,
  logout,
  setRightPlateChildrenType,
  setShowAuthModal,
  setIsFileLoading,
} = applicationSlice.actions;
